import React, { useContext, useEffect, useMemo, useRef, useState, useTransition } from "react";
import { useLocation, useNavigate } from "react-router";
import { buildHash } from "../../utils/filtering";
import { getConsoleStats } from "../../utils/console-filtering";
import { styled } from "@linaria/react";
import { hover } from "../interaction-styles";
import { SearchContext } from "./SearchContext";
import { MatchHighlight } from "./Highlight";
import { DeclarationsContext, sectionPath } from "../schema/DeclarationsContext";
import { getMetadataKeys, type GameContext } from "../../data/derived";
import { getNetworkKeys } from "../../data/network";
import { moduleLabel } from "../../data/modules";
import { KindIcon, IconKind, UiIcon } from "../kind-icon/KindIcon";
import type { SearchMode } from "../../utils/section-search";

export const SearchInput = styled.input`
  width: 100%;
  padding: 8px 14px;
  border: none;
  border-radius: 8px;
  background: var(--searchbox-background);
  color: var(--text);
  font-size: 16px;
  outline: none;
  transition: box-shadow 0.15s;

  ${hover("box-shadow: 0 0 0 2px color-mix(in srgb, var(--highlight) 19%, transparent);")}

  &:focus {
    box-shadow: 0 0 0 2px var(--highlight);
  }

  &::placeholder {
    color: var(--searchbox-placeholder);
  }
`;

declare global {
  interface Window {
    /** Set by search-prehydrate.js */
    stopSearchPrehydrate?: () => void;
  }
}

export interface SearchTag {
  tag: string;
  icon: IconKind;
  description: string;
  example: string;
  /** Only shown for games that have entities */
  entities?: boolean;
  /** Only shown for games that have network data */
  network?: boolean;
}

const SEARCH_TAGS: readonly SearchTag[] = [
  {
    tag: "module:",
    icon: "module",
    description: "Filter by module name",
    example: "e.g. module:client",
  },
  {
    tag: "offset:",
    icon: "offset",
    description: "Filter by byte offset",
    example: "e.g. offset:0x1A0",
  },
  {
    tag: "enumvalue:",
    icon: "enum-member",
    description: "Filter by enum member value",
    example: "e.g. enumvalue:4",
  },
  {
    tag: "metadata:",
    icon: "meta-tag",
    description: "Filter by metadata key name",
    example: "e.g. metadata:MPropertyFriendlyName",
  },
  {
    tag: "metadatavalue:",
    icon: "meta-variable",
    description: "Filter by metadata value",
    example: "e.g. metadatavalue:true",
  },
  {
    tag: "network:",
    icon: "meta-broadcast",
    description: "Filter by network property or value, =value for a whole one",
    example: "e.g. network:changeCallbacks, network:=Player or network:userGroups=Player",
    network: true,
  },
  {
    tag: "entity:",
    icon: "entity",
    description: "Filter entity classes by design name",
    example: "e.g. entity:trigger_",
    entities: true,
  },
  {
    tag: "input:",
    icon: "input",
    description: "Filter entities by their own inputs",
    example: "e.g. input:Enable",
    entities: true,
  },
  {
    tag: "output:",
    icon: "output",
    description: "Filter entities by their own outputs",
    example: "e.g. output:OnTrigger",
    entities: true,
  },
];

const CONSOLE_SEARCH_TAGS: readonly SearchTag[] = [
  {
    tag: "module:",
    icon: "module",
    description: "Filter by declaring module",
    example: "e.g. module:server",
  },
  {
    tag: "flag:",
    icon: "flag",
    description: "Filter by flag, -flag: excludes",
    example: "e.g. flag:cheat -flag:hidden",
  },
  {
    tag: "type:",
    icon: "convar",
    description: "Filter convars by value type",
    example: "e.g. type:float32",
  },
];

export function getSearchTags(
  mode: SearchMode,
  { hasEntities, hasNetwork }: { hasEntities: boolean; hasNetwork: boolean },
): readonly SearchTag[] {
  if (mode === "console") return CONSOLE_SEARCH_TAGS;
  return SEARCH_TAGS.filter((t) => (!t.entities || hasEntities) && (!t.network || hasNetwork));
}

function getLastWord(input: string): string {
  if (input === "" || input.endsWith(" ")) return "";
  return input.split(" ").at(-1) ?? "";
}

function shouldShowFirstLevelPopup(input: string): boolean {
  if (input.endsWith(" ")) return false;
  return !getLastWord(input).includes(":");
}

/** Only console tags can be negated with a leading "-" */
function negationPrefix(word: string, mode: SearchMode): "-" | "" {
  return mode === "console" && word.startsWith("-") ? "-" : "";
}

function filterTags(tags: readonly SearchTag[], lastWord: string, mode: SearchMode) {
  if (lastWord === "") return tags;
  const lower = lastWord.toLowerCase().slice(negationPrefix(lastWord, mode).length);
  return tags.filter((t) => t.tag.includes(lower));
}

/** Replaces the word being typed, or appends one after a space */
function replaceLastWord(inputValue: string, word: string): string {
  if (inputValue === "" || inputValue.endsWith(" ")) return inputValue + word;
  const parts = inputValue.split(" ");
  parts[parts.length - 1] = word;
  return parts.join(" ");
}

type ValueSuggestions = {
  tag: string;
  header: string;
  values: string[];
  counts?: Map<string, number>;
  /** What the site calls a value, shown beside it and matched too when it differs */
  label?: (value: string) => string;
};

function getSecondLevelContext(
  lastWord: string,
  suggestions: ValueSuggestions[],
  mode: SearchMode,
): { prefix: string; value: string; suggestions: ValueSuggestions } | null {
  const lower = lastWord.toLowerCase();
  const negated = negationPrefix(lower, mode);

  for (const sug of suggestions) {
    if (lower.startsWith(negated + sug.tag)) {
      return {
        prefix: negated + sug.tag,
        value: lastWord.slice(negated.length + sug.tag.length),
        suggestions: sug,
      };
    }
  }

  return null;
}

function getConsoleSuggestions(items: GameContext["consoleItems"]): ValueSuggestions[] {
  const { sorted, modules, flags, types } = getConsoleStats(items);
  return [
    { tag: "module:", header: "Modules", values: sorted.modules, counts: modules },
    { tag: "flag:", header: "Flags", values: sorted.flags, counts: flags },
    { tag: "type:", header: "Types", values: sorted.types, counts: types },
  ];
}

const SearchBoxWrapper = styled.div`
  position: relative;
  width: 100%;

  > svg {
    position: absolute;
    left: 12px;
    top: 50%;
    translate: 0 -50%;
    pointer-events: none;
    color: var(--searchbox-placeholder);
  }
`;

const MainSearchInput = styled(SearchInput)`
  padding-left: 36px;
`;

const SearchPlaceholder = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 14px 0 36px;
  pointer-events: none;
  color: var(--searchbox-placeholder);
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;

  > span {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  kbd {
    font-size: 12px;
    font-weight: 600;
    padding: 1px 6px;
    border-radius: 4px;
    border: 1px solid var(--searchbox-placeholder);
    opacity: 0.6;
    line-height: 1.4;
  }
`;

const TagPopup = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: var(--group);
  border: 1px solid var(--group-border);
  border-radius: 8px;
  box-shadow: var(--group-shadow);
  z-index: 200;
  overflow: hidden;
`;

const TagPopupHeader = styled.div`
  padding: 6px 12px 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-dim);
`;

const TagItem = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 7px 12px;
  border: none;
  background: transparent;
  color: var(--text);
  font-size: 14px;
  cursor: pointer;
  text-align: left;
  &[data-active],
  &:hover {
    background: var(--group-members);
  }
`;

const TagItemText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  flex: 1;
  min-width: 0;
`;

const TagItemName = styled.span`
  font-weight: 600;
  font-size: 13px;
`;

const TagItemDesc = styled.span`
  font-size: 12px;
  color: var(--text-dim);
`;

const TagItemExample = styled.span`
  font-size: 11px;
  color: var(--text-dim);
  opacity: 0.6;
  flex-shrink: 0;
`;

const ValueCount = styled.span`
  margin-left: auto;
  font-size: 12px;
  color: var(--text-dim);
  font-variant-numeric: tabular-nums;
`;

const ValuePopupList = styled.div`
  max-height: 200px;
  overflow-y: auto;
`;

function HighlightMatch({ text, query }: { text: string; query: string }) {
  if (!query) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <MatchHighlight>{text.slice(idx, idx + query.length)}</MatchHighlight>
      {text.slice(idx + query.length)}
    </>
  );
}

function ValueSuggestPopup({
  header,
  values,
  counts,
  label,
  activeIndex,
  onSelect,
  query,
}: {
  header: string;
  values: string[];
  counts?: Map<string, number>;
  label?: (value: string) => string;
  activeIndex: number;
  onSelect: (value: string) => void;
  query: string;
}) {
  return (
    // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role
    <TagPopup role="listbox" id="search-listbox" aria-label={header}>
      <TagPopupHeader>{header}</TagPopupHeader>
      <ValuePopupList>
        {values.map((v, i) => {
          const name = label?.(v);
          return (
            // oxlint-disable-next-line jsx-a11y/interactive-supports-focus
            <TagItem
              key={v}
              ref={i === activeIndex ? (el) => el?.scrollIntoView({ block: "nearest" }) : undefined}
              aria-selected={i === activeIndex}
              data-active={i === activeIndex || undefined}
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect(v);
              }}
            >
              <TagItemName>
                <HighlightMatch text={v} query={query} />
              </TagItemName>
              {name !== undefined && name !== v && (
                <TagItemDesc>
                  <HighlightMatch text={name} query={query} />
                </TagItemDesc>
              )}
              {counts && <ValueCount>{counts.get(v)}</ValueCount>}
            </TagItem>
          );
        })}
      </ValuePopupList>
    </TagPopup>
  );
}

function SearchTagPopup({
  tags,
  activeIndex,
  onSelect,
}: {
  tags: readonly SearchTag[];
  activeIndex: number;
  onSelect: (tag: string) => void;
}) {
  return (
    // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role
    <TagPopup role="listbox" id="search-listbox" aria-label="Search tag suggestions">
      <TagPopupHeader>Filters</TagPopupHeader>
      {tags.map((t, i) => (
        // oxlint-disable-next-line jsx-a11y/interactive-supports-focus
        <TagItem
          key={t.tag}
          ref={i === activeIndex ? (el) => el?.scrollIntoView({ block: "nearest" }) : undefined}
          aria-selected={i === activeIndex}
          data-active={i === activeIndex || undefined}
          onMouseDown={(e) => {
            e.preventDefault();
            onSelect(t.tag);
          }}
        >
          <KindIcon kind={t.icon} />
          <TagItemText>
            <TagItemName>{t.tag}</TagItemName>
            <TagItemDesc>{t.description}</TagItemDesc>
          </TagItemText>
          <TagItemExample>{t.example}</TagItemExample>
        </TagItem>
      ))}
    </TagPopup>
  );
}

export function SearchBox({
  className,
  mode = "schemas",
}: {
  className?: string;
  mode?: SearchMode;
}) {
  const { search } = useContext(SearchContext);
  const { game, declarations, entities, designNames, consoleItems, hasNetwork } =
    useContext(DeclarationsContext);
  const baseUrl = sectionPath(game, mode);
  const hasEntities = entities.length > 0;
  const tags = getSearchTags(mode, { hasEntities, hasNetwork });
  const [inputValue, setInputValue] = useState(search);
  const [isFocused, setIsFocused] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const [, startTransition] = useTransition();
  // The search this box last wrote to the URL. The input already shows it
  const ownSearchRef = useRef(search);

  // Sync input value from URL (back/forward navigation, clicking links)
  useEffect(() => {
    const urlSearch = new URLSearchParams(location.hash.slice(1)).get("search") ?? "";
    if (urlSearch === ownSearchRef.current) return;
    ownSearchRef.current = urlSearch;
    setInputValue(urlSearch);
  }, [location.hash]);

  const valueSuggestions = useMemo((): ValueSuggestions[] => {
    if (mode === "console") return getConsoleSuggestions(consoleItems);
    const list: ValueSuggestions[] = [
      { tag: "module:", header: "Modules", values: [...declarations.keys()], label: moduleLabel },
      { tag: "metadata:", header: "Metadata Keys", values: getMetadataKeys(declarations) },
      { tag: "network:", header: "Network Properties", values: getNetworkKeys(declarations) },
    ];
    if (hasEntities) {
      list.push({ tag: "entity:", header: "Entities", values: designNames });
    }
    return list;
  }, [mode, consoleItems, declarations, hasEntities, designNames]);

  const lastWord = getLastWord(inputValue);
  const filteredTags = filterTags(tags, lastWord, mode);
  const showFirstLevel =
    isFocused && shouldShowFirstLevelPopup(inputValue) && filteredTags.length > 0;

  const secondLevel = getSecondLevelContext(lastWord, valueSuggestions, mode);
  const secondLevelValues = useMemo(() => {
    if (!secondLevel) return [];
    const { values: list, label } = secondLevel.suggestions;
    if (secondLevel.value === "") return list;
    const lower = secondLevel.value.toLowerCase();
    return list.filter((v) => {
      if (v.toLowerCase().includes(lower)) return true;
      return label !== undefined && label(v).toLowerCase().includes(lower);
    });
  }, [secondLevel?.suggestions, secondLevel?.value]);
  const isExactMatch =
    secondLevel != null &&
    secondLevelValues.length === 1 &&
    secondLevelValues[0].toLowerCase() === secondLevel.value.toLowerCase();
  const showSecondLevel =
    isFocused && secondLevel != null && secondLevelValues.length > 0 && !isExactMatch;

  const showPopup = showFirstLevel || showSecondLevel;
  const popupLength = showFirstLevel ? filteredTags.length : secondLevelValues.length;

  const applyNewValue = (newValue: string) => {
    setInputValue(newValue);
    const replace = inputValue !== "" || newValue === "";

    // Other hash params, like the convars page kind filter, stay when searching on the same page.
    // A directly loaded prerendered page can have a trailing slash
    const samePage = location.pathname.replace(/\/$/, "") === baseUrl;
    const params = Object.fromEntries(new URLSearchParams(samePage ? location.hash.slice(1) : ""));
    const hash = buildHash({ ...params, name: null, search: newValue });
    ownSearchRef.current = newValue;

    startTransition(() => {
      navigate({ pathname: baseUrl, hash }, { replace });
    });

    setActiveIndex(0);
    ref.current?.focus();
  };

  const handleTagSelect = (tag: string) => {
    applyNewValue(replaceLastWord(inputValue, negationPrefix(lastWord, mode) + tag));
  };

  const handleValueSelect = (value: string) => {
    if (!secondLevel) return;
    applyNewValue(replaceLastWord(inputValue, secondLevel.prefix + value));
  };

  const onChange: React.ChangeEventHandler<HTMLInputElement> = ({ target: { value } }) => {
    setIsFocused(true);
    applyNewValue(value);
  };

  const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      if (showPopup) {
        setIsFocused(false);
      } else {
        ref.current?.blur();
      }
      return;
    }
    if (!showPopup) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, popupLength - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" || e.key === "Tab") {
      // Tab completes only a word being typed. Otherwise it moves focus to the next element
      if (e.key === "Tab" && lastWord === "") return;
      e.preventDefault();

      if (showFirstLevel && filteredTags[activeIndex]) {
        handleTagSelect(filteredTags[activeIndex].tag);
      } else if (showSecondLevel && secondLevelValues[activeIndex]) {
        handleValueSelect(secondLevelValues[activeIndex]);
      }
    }
  };

  const ref = useRef<HTMLInputElement>(null);

  // React owns the input now, so this stops the pre-hydration script from rewriting the history entry
  useEffect(() => {
    window.stopSearchPrehydrate?.();
  }, []);

  // "/" focuses the search from anywhere outside an input
  useEffect(() => {
    const listener = (e: KeyboardEvent) => {
      if (e.key !== "/" || e.ctrlKey || e.metaKey || e.altKey) return;
      if (!(document.activeElement instanceof HTMLInputElement)) {
        e.preventDefault();
        ref.current?.focus();
      }
    };
    document.addEventListener("keydown", listener);
    return () => document.removeEventListener("keydown", listener);
  }, []);

  return (
    <SearchBoxWrapper className={className}>
      <UiIcon name="search" />
      {!inputValue && !isFocused && (
        <SearchPlaceholder>
          Type <kbd>/</kbd>{" "}
          <span>to search {mode === "console" ? "convars and commands" : "schemas"}</span>
        </SearchPlaceholder>
      )}
      <MainSearchInput
        id="main-search"
        type="search"
        ref={ref}
        value={inputValue}
        onChange={onChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onKeyDown={onKeyDown}
        // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role
        role="combobox"
        tabIndex={0}
        aria-label="Search"
        aria-controls={showPopup ? "search-listbox" : undefined}
        aria-autocomplete="list"
        aria-expanded={showPopup}
        spellCheck={false}
        autoCorrect="off"
        autoCapitalize="off"
      />
      {showFirstLevel && (
        <SearchTagPopup tags={filteredTags} activeIndex={activeIndex} onSelect={handleTagSelect} />
      )}
      {showSecondLevel && secondLevel && (
        <ValueSuggestPopup
          header={secondLevel.suggestions.header}
          values={secondLevelValues}
          counts={secondLevel.suggestions.counts}
          label={secondLevel.suggestions.label}
          activeIndex={activeIndex}
          onSelect={handleValueSelect}
          query={secondLevel.value}
        />
      )}
    </SearchBoxWrapper>
  );
}
